import { makeAutoObservable } from 'mobx';
import { z } from 'zod';
import type { RequireAllProps } from '../../../../../store/utils/Dto';
import extractValue, {
  type EventOrValue,
} from '../../../../../store/utils/extractValue';
import Validator from '../../../../../store/utils/Validator';
import { errorHandler } from '../../../../../utils/errors';
import type Cdn from '../../../../account/store/services/Cdn';
import type S3File from './S3File';
import VersionFile, {
  type VersionFileConstructorDto,
  type VersionFileDto,
} from './VersionFile';
import type Versions from './Versions';

export default class Version {
  static Schema = z
    .object({
      name: z.string().min(1),
      files: z.array(z.any()),
      canBeChanged: z.any(),
      previousFiles: z.any(),
    })
    .superRefine((data, ctx) => {
      const message = validateFiles(
        data.files,
        data.canBeChanged,
        data.previousFiles,
      );
      if (message) {
        ctx.addIssue({ code: 'custom', path: ['files'], message });
      }
    });

  id = Date.now();
  name = '1.0';
  changelog = '';
  files: VersionFile[] = [];
  isTest = false;

  areFilesChanged = false;
  isChanged = false;
  isNew = false;
  keepVersionId = false;
  forceEditMode = false;

  private readonly validator = new Validator(this, Version.Schema);

  constructor(
    { files = [], ...attributes }: VersionConstructorDto,
    private readonly versionList: Versions,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.files = files.map((file) => this.makeFile(file));
    Object.assign(this, attributes);

    this.validate();
  }

  get canBeChanged() {
    return this.isLatest || this.isNew || this.forceEditMode;
  }

  get errors() {
    return this.validator.errorsForEnabledFields;
  }

  get isValid(): boolean {
    return this.validator.isValid;
  }

  get allErrorsAsStrings(): string[] {
    return this.validator.allErrorsAsStrings;
  }

  get isFirst() {
    return this.versionList.firstVersion === this;
  }

  get isLatest() {
    return this.versionList.latestVersion?.id === this.id;
  }

  get previousFiles(): VersionFile[] {
    const files: VersionFile[] = [];
    for (const ver of this.versionList.getPreviousVersions(this.id)) {
      for (const file of ver.files) {
        files.push(file);
      }
    }

    return files.filter((f) => f.url);
  }

  addCdnSettings(cdn: Cdn) {
    this.files.forEach((file) => file.addCdnSettings(cdn));
  }

  async delete() {
    await this.versionList.deleteVersion(this);
  }

  ensureEmptyFileInTheBottom() {
    if (!this.canBeChanged) {
      this.files = this.files.filter((file) => !file.isEmpty);
      return;
    }

    if (this.files.length === 0) {
      this.files.push(this.makeFile({}));
      return;
    }

    const isAllFilesValid = this.files.every((file) => file.isValid);
    if (!isAllFilesValid) {
      this.files = this.files.filter((file) => !file.isEmpty);
      return;
    }

    const emptyFiles = this.files.filter((file) => file.isEmpty);
    if (emptyFiles.length === 0) {
      this.files.push(this.makeFile({}));
    } else if (emptyFiles.length > 1) {
      this.files = this.files.filter((file) => !file.isEmpty);
      this.files.push(this.makeFile({}));
    }
  }

  handleBlur(e: EventOrValue<keyof VersionDto>) {
    const { name } = extractValue(e);
    this.validator.enableFieldValidation(name);
  }

  handleChange(e: EventOrValue) {
    const { name, value } = extractValue(e);

    Object.assign(this, { [name]: value });

    this.validator.validate();

    this.markChanged();
  }

  markAsSaved() {
    this.isNew = false;
    this.isChanged = false;
    this.areFilesChanged = false;
  }

  markChanged() {
    this.isChanged = true;
    this.versionList
      .markVersionChanged(this)
      .catch(errorHandler('Version changing error'));
  }

  onFileChanged(file?: VersionFile) {
    if (file?.isDeleted) {
      this.files = this.files.filter((f) => !f.isDeleted && !f.isEmpty);
    }

    this.validator.enableFieldValidation('files');
    this.validator.validate();

    this.ensureEmptyFileInTheBottom();

    this.markChanged();
    this.areFilesChanged = true;
  }

  replaceFileByS3Files(file: VersionFile, s3Files: S3File[]) {
    if (s3Files.length === 0) {
      return;
    }

    const files = s3Files.map((s3File) => {
      return this.makeFile({
        ...s3File.toJSON(),
        existenceState: 'exists',
      });
    });
    const existedIndex = this.files.indexOf(file);

    if (existedIndex === -1) {
      this.files.push(...files);
    } else {
      this.files.splice(existedIndex, 1, ...files);
    }

    this.onFileChanged();
  }

  toJSON(): RequireAllProps<VersionDto> {
    return {
      id: this.id,
      name: this.name,
      changelog: this.changelog || undefined,
      isTest: this.isTest || undefined,
      files: this.files.filter((f) => !f.isEmpty).map((file) => file.toJSON()),
    };
  }

  updateIdIfNecessary() {
    // Legacy products which have no versions previously
    const MIN_VERSION = 1_580_515_200_000;

    if (this.areFilesChanged && !this.keepVersionId) {
      const baseId = this.id > 1 ? Math.max(this.id, MIN_VERSION) : Date.now();
      this.id = baseId + 1;
    }
  }

  validate(): string[] {
    this.validator.validate();

    return this.validator.allErrorsAsStrings;
  }

  private makeFile(dto: VersionFileConstructorDto) {
    const id = this.versionList.generateFileId();
    return new VersionFile(
      {
        existenceState: this.isNew ? undefined : 'exists',
        ...dto,
        id,
      },
      this.onFileChanged,
    );
  }
}

export interface VersionDto {
  changelog?: string;
  files?: VersionFileDto[];
  id?: number;
  isTest?: boolean;
  name?: string;
}

export interface VersionConstructorDto extends VersionDto {
  forceEditMode?: boolean;
  isNew?: boolean;
}

function validateFiles(
  allFiles: VersionFile[],
  canBeChanged: boolean,
  previousFiles: VersionFile[],
): string | undefined {
  if (!canBeChanged) {
    return undefined;
  }

  const thisVerFiles = allFiles.filter((file) => !file.isEmpty);
  const previousVerFiles = (previousFiles || []).filter(
    (file) => !file.isEmpty,
  );

  return (
    requireOneFileAtLeast(thisVerFiles) ||
    requireUniqueUrlInThisVersion(thisVerFiles) ||
    requireUniqueUrlInAllVersions([...thisVerFiles, ...previousVerFiles]) ||
    requireUniqueFileNames([...thisVerFiles, ...previousVerFiles]) ||
    requireFilesAreChecked(thisVerFiles) ||
    requireFilesAreValid(thisVerFiles) ||
    undefined
  );

  function requireOneFileAtLeast(files: VersionFile[]): string {
    return files.length === 0 ? 'At least one URL is required' : '';
  }

  function requireUniqueUrlInThisVersion(files: VersionFile[]): string {
    const urls = files.map((file) => file.url);
    return arrayHasDuplicates(urls) ? 'Version contains duplicated URL' : '';
  }

  function requireUniqueUrlInAllVersions(files: VersionFile[]): string {
    const urls = files.map((file) => file.url);
    return arrayHasDuplicates(urls)
      ? 'Previous versions already contain specified URL'
      : '';
  }

  function requireUniqueFileNames(files: VersionFile[]): string {
    const urls = files.map((file) => file.name);
    const duplicates = findDuplicates(urls).join(', ');
    return duplicates
      ? `An archive name should be unique. Duplicates found: ${duplicates}`
      : '';
  }

  function requireFilesAreChecked(files: VersionFile[]): string {
    const filesAreChecked = files.some((file) => !file.isChecking);
    return filesAreChecked ? '' : 'Checking some URLs…';
  }

  function requireFilesAreValid(files: VersionFile[]): string {
    const filesAreValid = files.some((file) => file.isValid);
    return filesAreValid ? '' : "One of URL isn't correct";
  }
}

function arrayHasDuplicates(array: any[]): boolean {
  return findDuplicates(array).length > 0;
}

function findDuplicates(array: any[]): any[] {
  return array.filter((item, index) => array.indexOf(item) !== index);
}
